/**
 * Brand Bible v2 — wizard types.
 * Mirrors contract §2 (schema), §4.4 (API), §6 (score). One source of truth for shapes;
 * the backend registry in §2.2 is authoritative for table names.
 */

export type BbTable =
  | 'identity'
  | 'voice_profile'
  | 'services'
  | 'locations'
  | 'credentials'
  | 'offers'
  | 'review_stats'
  | 'case_studies'
  | 'compliance_rules'
  | 'visual_identity'
  | 'chunks';

export type BbSource = 'intake' | 'discovered' | 'inferred' | 'performance' | 'client_edit';
export type BbConfidence = 'high' | 'medium' | 'low';

/** Contract §2.1 — the universal entry metadata envelope (server-owned fields readonly here). */
export interface BbEnvelope {
  id: string;
  tenant_id: string;
  location_scope: string; // 'all' | location_key
  section: 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G' | 'H' | 'I';
  source: BbSource;
  verified_by: string | null;
  verified_at: string | null;
  approved_for_public: boolean;
  confidence: BbConfidence;
  effective_from: string | null;
  effective_to: string | null;
  version: number;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

/** Typed payloads per table (create/patch bodies). Kept to wizard-v1 fields. */
export interface IdentityPayload {
  legal_name?: string;
  dba?: string;
  founded_year?: number;
  /** Appendix A.5: bb_identity.owner is also a merge-patch column — `name`
   * is required on the STORED row but a PATCH may legitimately send just
   * `{title}` or `{on_camera_ok}` alone (client discipline: changed subkeys
   * only, never a `{...current, ...patch}` spread), so it's optional here. */
  owner?: { name?: string; title?: string; on_camera_ok?: boolean };
  team_size?: number;
  website?: string;
  booking_url?: string;
  /** Appendix A.5: bb_identity.socials is a merge-patch column server-side —
   * a value of `null` deletes that key (RFC-7396 style); omit a key entirely
   * to leave it untouched. Never send the whole map from a client copy. */
  socials?: Record<string, string | null>;
  /** §7.5 fault fix: v2 homes for the legacy contact_info.{phone,email} and
   * description columns, which had no typed home before. */
  phone?: string;
  email?: string;
  brand_overview?: string;
  /** READ-ONLY (set by the web gather, changed only by accept/reject): web-found values waiting
   * for a person. Never sent in a write payload. */
  pending_fields?: Record<string, PendingField>;
}

export interface VoiceProfilePayload {
  voice_summary?: string;
  attributes?: string[];
  reading_level?: string;
  person?: string;
  customer_term?: string;
  humor_level?: string;
}

export interface ServicePayload {
  name?: string;
  category?: string;
  emergency?: boolean;
  price_policy?: 'publish' | 'quote_only';
  price_low?: number | null;
  price_high?: number | null;
  avg_job_value_range?: string;
}

export interface LocationPayload {
  location_key?: string;
  address?: string;
  metro?: string;
  service_radius_miles?: number;
  hours?: Record<string, string>;
  tracking_phone?: string;
  gbp_url?: string;
}

export type CredentialKind = 'license' | 'insurance' | 'certification' | 'award';
/** Optional text/date columns are `| null` on purpose: `null` is how a caller
 *  CLEARS a filled column (`_apply_update` binds it straight to SQL NULL), and
 *  it is also what the row reads back as when the column was never filled —
 *  so the same type honestly describes both directions. `undefined` means
 *  "not in this patch", which `JSON.stringify` drops; it can never clear. */
export interface CredentialPayload {
  kind?: CredentialKind;
  name?: string;
  number?: string | null;
  issuer_or_state?: string | null;
  expiry?: string | null;
  display_required?: boolean;
  proof_ref?: string | null;
}

export type OfferKind = 'dispatch_fee' | 'financing' | 'guarantee' | 'promotion';
export interface OfferPayload {
  kind?: OfferKind;
  title?: string;
  /** Guarantees: exact wording — agents quote, never paraphrase. 'none' records explicit-none. */
  verbatim_text?: string;
  amount?: string;
  terms?: string;
  provider?: string;
  channels?: string[];
  effective_from?: string | null;
  effective_to?: string | null;
}

export interface ReviewStatPayload {
  platform?: string;
  review_count?: number;
  /** NUMERIC(3,2), nullable — `null` clears a previously claimed rating. */
  rating?: number | null;
  as_of?: string; // mandatory server-side (provability rule)
}

export interface CaseStudyPayload {
  job_type?: string;
  result?: string | null;
  approved_quote?: string | null;
  client_permission?: boolean;
  metro?: string | null;
}

export type ComplianceKind =
  | 'banned_claim'
  | 'regulated'
  | 'excluded_service'
  | 'excluded_area'
  | 'sensitive_topic'
  | 'platform_note';
export interface ComplianceRulePayload {
  kind?: ComplianceKind;
  rule_text?: string;
  applies_to?: string;
}

/** §H — repeatable logo reference row (pointer only, never a file). */
export type LogoRefKind = 'primary' | 'wordmark' | 'mark' | 'favicon' | 'other';
export interface LogoRef {
  kind: LogoRefKind;
  /** URL or asset pointer — never a file. */
  reference: string;
  usage_note?: string;
}

/** §H — role-keyed hex color map, exactly six roles. */
export type ColorTokenRole = 'primary' | 'secondary' | 'accent' | 'background' | 'text' | 'additional';
export type ColorTokens = Record<ColorTokenRole, string>;

/** §H — all fields optional: writes are PATCH-style, a caller legitimately sends one field at a time. */
export interface VisualIdentityPayload {
  logo_refs?: LogoRef[];
  color_tokens?: Partial<ColorTokens>;
  font_names?: string[];
  photo_library_ref?: string;
  /** NOT NULL DEFAULT TRUE today; `null` also accepted ahead of a queued nullability migration. */
  real_photos_only?: boolean | null;
  badge_usage_notes?: string;
}

export type ChunkKind =
  | 'do_say'
  | 'never_say'
  | 'formatting_rule'
  | 'golden_example'
  | 'rejected_example'
  | 'icp_note'
  | 'seasonality_note'
  | 'area_note'
  | 'competitor'
  | 'headline'
  | 'cta_preference'
  | 'review_response_template'
  | 'escalation_trigger'
  | 'market_narrative';
export interface ChunkPayload {
  kind?: ChunkKind;
  title?: string;
  body?: string;
  channel?: string | null;
  why?: string;
  public_mention?: boolean;
  section?: BbEnvelope['section'];
}

export type BbPayloadMap = {
  identity: IdentityPayload;
  voice_profile: VoiceProfilePayload;
  services: ServicePayload;
  locations: LocationPayload;
  credentials: CredentialPayload;
  offers: OfferPayload;
  review_stats: ReviewStatPayload;
  case_studies: CaseStudyPayload;
  compliance_rules: ComplianceRulePayload;
  visual_identity: VisualIdentityPayload;
  chunks: ChunkPayload;
};

export type BbEntry<T extends BbTable = BbTable> = BbEnvelope & BbPayloadMap[T];

/**
 * Patch-shape payload for `upsertEntry`/`patchEntry` — the table's typed
 * payload plus the PATCH-writable envelope subset. Backend `patch_entry`
 * explicitly allows these on PATCH (`entries.py:233`:
 * `spec["columns"] + ENVELOPE_WRITABLE + ("approved_for_public",)`);
 * `location_scope`/`effective_from`/`effective_to` come from
 * `ENVELOPE_WRITABLE` (`registry.py:10`). `verified_by`/`verified_at` are
 * deliberately excluded — those are verify-endpoint-only, never patchable.
 */
export type BbPatchPayload<T extends BbTable> = Partial<BbPayloadMap[T]> &
  Partial<Pick<BbEnvelope, 'approved_for_public' | 'location_scope' | 'effective_from' | 'effective_to'>>;

/** Contract §6 — score object returned in-band on every mutation and by GET /score. */
export interface ScoreComponent {
  id: string; // 'A1', 'B5', 'I', ...
  label: string;
  points: number;
  max: number;
}
export interface ScoreGap {
  id: string;
  label: string;
  missing: number;
}
export interface BibleScore {
  score: number;
  components: ScoreComponent[];
  gaps: ScoreGap[];
}

/** Section rollup used by the rail + review screen (derived client-side from components). */
export interface SectionPoints {
  section: string;
  label: string;
  points: number;
  max: number;
}

export interface MutationResponse<T extends BbTable = BbTable> {
  entry: BbEntry<T>;
  /** null only when score computation failed server-side (mutation still committed). */
  score: BibleScore | null;
  warning?: string;
}

export interface BibleResponse {
  tenant_id: string;
  /** Per-table entry lists — the API field is named  (contract §4.4). */
  tables: { [K in BbTable]?: BbEntry<K>[] };
  score: BibleScore;
}

/** A web-gathered identity value waiting for a person (`bb_identity.pending_fields[<field>]`). */
export interface PendingField {
  value: string;
  run_id: string | null;
  at: string;
}

/** `GET /gather/eligibility` (R11, R17). */
export interface GatherEligibility {
  tenant_allowed: boolean;
  eligible: boolean;
  reason: null | 'no_website' | 'filled_v1';
  cooldown_s: number;
}

export interface GatherSourceSummary {
  kind: 'website' | 'gbp';
  url?: string;
  method: string | null;
  outcome: string;
  reason?: string | null;
  pages: { url: string; outcome: string; chars: number }[];
  created: number;
  skipped: number;
  suppressed: number;
  conflicts: number;
  rejected: number;
  blocked: number;
}

/** `POST /gather` 200 body (R14). Saved and rejected values are never echoed; a conflict carries the found value. */
export interface GatherResponse {
  tenant_id: string;
  run_id: string;
  mode: 'gather';
  activated: boolean;
  seeded: number;
  totals: {
    created: number;
    skipped: number;
    suppressed: number;
    conflicts: number;
    rejected: number;
    blocked: number;
  };
  sources: GatherSourceSummary[];
  created_detail: { table: string; kind: 'row' | 'field'; field?: string; entry_id: string }[];
  rejected: { table: string | null; field: string | null; reason: string }[];
  conflicts: { table: string; field: string | null; found?: string | null; source_url?: string | null }[];
  browserbase: { sessions: number; seconds: number };
  failures: { target: string; reason: string }[];
  pages_scraped: number;
  entries_drafted: number;
}

/** One `GET /sources` row (never carries the value fingerprint). */
export interface BbSourceRow {
  entry_id: string;
  table_name: string;
  field_name: string;
  source_kind: 'web' | 'gbp_api' | 'questionnaire';
  source_url: string | null;
  /** "Questionnaire sheet · <tab> · row N" for a client questionnaire answer; null for web rows. */
  source_ref?: string | null;
  quote: string;
  matched_in: string;
  fetched_via: string;
  run_id: string;
  at: string;
}

export interface EnrichFailure {
  target: string;
  reason: string;
}
export interface EnrichResponse {
  tenant_id: string;
  pages_scraped: number;
  gbp_found: boolean;
  entries_drafted: number;
  skipped_existing: number;
  failed: number;
  failures: EnrichFailure[];
}

export const WIZARD_STEPS: {
  step: number;
  key: string;
  section: BbEnvelope['section'] | null;
  title: string;
  meta: string;
}[] = [
  { step: 1, key: 'identity', section: 'A', title: 'Identity', meta: '20 pts' },
  { step: 2, key: 'voice', section: 'B', title: 'Voice', meta: '22 pts' },
  { step: 3, key: 'offers', section: 'C', title: 'Offers', meta: '15 pts' },
  { step: 4, key: 'proof', section: 'D', title: 'Proof', meta: '12 pts' },
  { step: 5, key: 'compliance', section: 'G', title: 'Compliance', meta: '15 pts' },
  { step: 6, key: 'review', section: null, title: 'Review & score', meta: 'gaps + verification' },
];

export const LAUNCH_THRESHOLD = 60;
export const STANDARD_THRESHOLD = 80;
export const BRAND_BIBLE_SCORE_MAX = 93;

/** GET /questionnaire/info: the panel's state. `sheet` only when a sheet exists for this environment. */
export interface QuestionnaireInfo {
  eligible: boolean;
  reason: null | 'no_website' | 'filled_v1';
  sheet: { url: string; template_version: string; created_at: string | null; last_imported_at: string | null } | null;
  latest_version: string;
  drive_ready: boolean;
}

/** POST /questionnaire/sheet. */
export interface QuestionnaireSheetResult {
  url: string;
  created: boolean;
  template_version: string;
}

/** The run summary of POST /questionnaire/import and POST /questionnaire (gather-shaped, no site fields). */
export interface QuestionnaireRunSummary {
  tenant_id: string;
  run_id: string;
  mode: 'questionnaire';
  activated: boolean;
  seeded: number;
  totals: GatherResponse['totals'];
  sources: { kind: 'questionnaire'; via: 'sheets_api' | 'upload'; outcome: string; rows_read: number; cells_answered: number; ignored: number }[];
  created_detail: GatherResponse['created_detail'];
  /** Parse rejections {tab,row,key,reason} (never the answer) and write rejections {table,field,reason}. */
  rejected: { tab?: string; row?: number; key?: string; table?: string | null; field?: string | null; reason: string }[];
  conflicts: { table: string; field: string | null; found?: string | null }[];
  failures: { target: string; reason: string }[];
  /** Answers an existing, reviewed row could not take automatically: shown so a person adds them by hand. */
  not_written?: { table: string; field: string | null; found?: unknown }[];
  entries_drafted: number;
}
